<script setup lang="ts">
import type { EventRepresentativeStoryblok } from '@/types'

interface Props {
  representatives: EventRepresentativeStoryblok[]
  isFeatured?: boolean
}

defineProps<Props>()
const emit = defineEmits<{ (e: 'click', value: boolean): void }>()
const handleClick = () => {
  emit('click', false)
}
</script>

<template>
  <section class="event-representatives">
    <h5 class="event-representatives__heading">
      {{ $t('common.events.representatives_heading') }}
    </h5>
    <div
      v-for="represent in representatives"
      :key="represent._uid"
      class="event-representatives__item"
    >
      <BaseNuxtImage
        v-if="represent.content.image"
        class="event-representatives__img"
        width="80"
        height="80"
        :src="represent.content.image.filename"
        :alt="represent.content.image.alt"
      />
      <div>
        <p class="event-representatives__info">
          {{ represent.content.name }}
        </p>
        <p class="event-representatives__info">
          {{ represent.content.role }}
        </p>
        <BaseLink
          :address="represent.content.linkedin_link"
          target="_blank"
          rel="noopener"
          class="event-representatives__icon linkedin"
        >
          <div class="event-representatives__icon linkedin" />
        </BaseLink>
      </div>
    </div>
    <div class="event-representatives__btn-container">
      <BaseButton
        :has-shadow="isFeatured"
        class="event-representatives__btn"
        @click="handleClick"
      >
        <template #icon>
          <img
            width="16"
            height="16"
            class="event-representatives-preview__icon"
            src="@/assets/icons/16-chevron-left-white.svg?url"
            alt="chevron"
          />
        </template>
      </BaseButton>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.event-representatives {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 40px;

  @media (min-width: $breakpoint-lg) {
    @include shadow-flat-soft;

    margin: 0;
    background: white;
    padding: 32px;
    z-index: 10;
    border-radius: 16px;
    width: 338px;
    gap: 32px;
  }

  &__heading {
    @include heading-subheading;

    @media (min-width: $breakpoint-lg) {
      text-align: center;
      margin: 0;
      padding-bottom: 16px;
      border-bottom: 1px solid var(--gray-300);
    }
  }

  &__item {
    display: flex;
    gap: 20px;
  }

  &__info {
    margin: 0;

    @include paragraph-xs;

    &:first-of-type {
      @include paragraph-s;

      font-weight: var(--font-weight-paragraph-bold);
    }
  }

  &__img {
    width: 80px;
    height: 80px;
    border-radius: 2137px;
    object-fit: contain;
  }

  &__icon {
    display: block;
  }

  &__btn-container {
    margin-left: auto;
    z-index: 100;
    transform: translateY(50%);

    @media (min-width: $breakpoint-lg) {
      display: none;
    }
  }

  &__btn {
    transform: rotate(90deg);
  }
}

.linkedin {
  @include icon('@/assets/icons/24-social-linkedin-navy.svg', 20px, 20px);

  margin-top: 2px;
  text-decoration: none;
  background-size: auto;
}
</style>
